import React, { useState, useEffect, useCallback } from "react";
import SendModalComponent from "./SendModalComponent";
import { fetchComments, createComment, CommentItem } from "../../api/Comment";
import { toast } from "react-toastify";

interface CommentProps {
  productId?: number;
}

type SortBy = "newest" | "oldest";

export default function Comment({ productId }: CommentProps) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page] = useState(1);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>("newest");

  const loadComments = useCallback(async () => {
    if (!productId) return;

    setLoading(true);
    try {
      const response = await fetchComments(productId, page);
      if (response?.data) {
        const items = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.items)
            ? response.data.items
            : [];

        const sortedData = [...items].sort((a: CommentItem, b: CommentItem) => {
          const aTime = new Date(a.created_at).getTime();
          const bTime = new Date(b.created_at).getTime();
          return sortBy === "newest" ? bTime - aTime : aTime - bTime;
        });

        setComments(sortedData);
        setTotal(typeof response.data.total === "number" ? response.data.total : sortedData.length);
      } else {
        setComments([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Failed to load comments:", error);
    } finally {
      setLoading(false);
    }
  }, [productId, page, sortBy]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSendReply = async (parentId: number) => {
    if (!productId || !replyContent.trim()) return;

    try {
      const success = await createComment(productId, replyContent, parentId);
      if (success) {
        setReplyContent("");
        setReplyingTo(null);
        await loadComments();
      }
    } catch (error) {
      toast.error("Gửi phản hồi thất bại. Vui lòng thử lại sau.", {
        position: "bottom-right",
      });
      console.error("Failed to send reply:", error);
    }
  };

  return (
    <div className="styles_productComment__SmsL7 d-none">
      <div className="styles_productCommentHeader___9R7A MuiBox-root css-0">
        <div className="styles_pCHLeft__p9V75 MuiBox-root css-0">
          <h3 className="MuiTypography-root styles_pCHTitle__I_W_C MuiTypography-h3">
            Thảo luận <span>({total})</span>
          </h3>

          <div className="styles_pCHSort__1pX2g MuiBox-root css-0">
            <span className="styles_pCHSortLabel__H2A80">Sắp xếp theo:</span>
            <div className="styles_pCHSortList__bX9Lp MuiBox-root css-0">
              <span
                className="styles_pCHSortItem__HAn94"
                onClick={() => setSortBy("newest")}
                role="button"
              >
                Mới nhất
              </span>
              <span
                className="styles_pCHSortItem__HAn94"
                onClick={() => setSortBy("oldest")}
                role="button"
              >
                Cũ nhất
              </span>
            </div>
          </div>
        </div>

        <div className="styles_pCHRight__q_C5J MuiBox-root css-0">
          <button
            className="styles_base__Pdm9S styles_contained__4kDf4 styles_colorGreenish__fz_CF styles_small__QTUmD"
            type="button"
            onClick={() => setOpen(true)}
          >
            Gửi câu hỏi
          </button>
        </div>
      </div>

      <div className="styles_mainComment__7SwfO MuiBox-root css-0">
        <div className="styles_readMoreWrapper__26uhD">
          <ul className="MuiList-root styles_commentList__byBaU MuiList-padding">
            {loading ? (
              <div style={{ padding: 16, textAlign: "center" }}>Đang tải bình luận...</div>
            ) : comments.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                Chưa có bình luận nào cho sản phẩm này. Hãy là người đầu tiên bình luận!
              </div>
            ) : (
              comments.map((item: any) => (
                <li key={item.id} className="MuiListItem-root styles_commentItem__nWOGk">
                  <div className="styles_commentInner__tt6x7 MuiBox-root css-0">
                    <div className="styles_commentAvatar__fa0Mc styles_hasChildBranch__weVqR MuiBox-root css-0">
                      <img
                        src={
                          item.customer?.avatar ||
                          "https://cdn-web-next.thuocsi.vn/images/customerv2_avatar_qa.svg"
                        }
                        alt="avatar"
                        className="styles_commentAvatarImg__jlxDQ"
                      />
                    </div>

                    <div className="styles_commentInfo__VUjW7 MuiBox-root css-0">
                      <h4 className="MuiTypography-root styles_commentName__X_cRE MuiTypography-h4">
                        {item.customer?.fullname || "Khách hàng"}
                      </h4>

                      <div className="styles_commentContent__jaaOa">{item.comment}</div>

                      <div className="styles_commentUtils__pwwsc">
                        <span className="styles_commentTime___jR8W">
                          {new Date(item.created_at).toLocaleDateString("vi-VN")}
                        </span>

                        <div
                          role="button"
                          className="styles_commentReply__JftrS"
                          onClick={() => setReplyingTo(item.id)}
                        >
                          Trả lời
                        </div>
                      </div>
                    </div>
                  </div>

                  {replyingTo === item.id && (
                    <div
                      className="styles_hasReplies__u4ALo styles_replyInput__9WyqP styles_replyInput-type-COMMENT__Q_7lM MuiBox-root css-0"
                      style={{ paddingLeft: 40 }}
                    >
                      <div className="styles_replyInfo__Pyi5E MuiBox-root css-0">
                        <h4 className="MuiTypography-root styles_replyName__cFBJi MuiTypography-h4">
                          Đang phản hồi: <span>{item.customer?.fullname || "Khách hàng"}</span>
                        </h4>

                        <form
                          className="styles_replyForm__RhKnd"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleSendReply(item.id);
                          }}
                        >
                          <div className="styles_replyFormInput__Mekc6 MuiBox-root css-0">
                            <div className="styles_formTextarea__tEzx5 MuiBox-root css-0">
                              <textarea
                                rows={2}
                                className="styles_input__guBXf"
                                maxLength={200}
                                placeholder="* Vui lòng nhập nội dung phản hồi bằng tiếng Việt có dấu (Bắt buộc)"
                                name="reply"
                                style={{ height: 64 }}
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                              />
                              <div className="styles_textareaCount__dhFX3">
                                {replyContent.length}/200
                              </div>
                            </div>
                          </div>

                          <div className="styles_submit__KDOGl MuiBox-root css-0">
                            <button
                              className="styles_base__Pdm9S styles_contained__4kDf4 styles_colorGreenish__fz_CF styles_small__QTUmD"
                              style={{ marginRight: 8 }}
                              type="submit"
                            >
                              Gửi phản hồi
                            </button>
                            <button
                              className="styles_base__Pdm9S styles_contained__4kDf4 styles_colorSecondary__js_Iq styles_small__QTUmD"
                              type="button"
                              onClick={() => setReplyingTo(null)}
                            >
                              Hủy
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}

                  {item.children && item.children.length > 0 && (
                    <div className="styles_loadMoreReply__0gNh3 styles_readMoreWrapper__26uhD">
                      {item.children.map((child: any) => (
                        <li
                          key={child.id}
                          className="MuiListItem-root styles_commentItem__nWOGk styles_subCommentItem___bLrj"
                          style={{ paddingLeft: 40 }}
                        >
                          <div className="styles_commentInner__tt6x7 MuiBox-root css-0">
                            <div className="styles_commentAvatar__fa0Mc MuiBox-root css-0">
                              <img
                                src={
                                  child.customer_id === 0
                                    ? "https://cdn-web-next.thuocsi.vn/images/employee_avatar_qa.svg"
                                    : child.customer?.avatar ||
                                      "https://cdn-web-next.thuocsi.vn/images/customerv2_avatar_qa.svg"
                                }
                                alt="avatar"
                                className="styles_commentAvatarImg__jlxDQ"
                              />
                            </div>

                            <div className="styles_commentInfo__VUjW7 MuiBox-root css-0">
                              <h4 className="MuiTypography-root styles_commentName__X_cRE MuiTypography-h4">
                                {child.customer_id === 0
                                  ? "Dược Tâm An - Hỗ trợ"
                                  : child.customer?.fullname || "Khách hàng"}
                              </h4>

                              <div className="styles_commentContent__jaaOa">
                                <div
                                  style={{ marginBottom: 0 }}
                                  dangerouslySetInnerHTML={{ __html: child.comment }}
                                />
                              </div>

                              <div className="styles_commentUtils__pwwsc">
                                <span className="styles_commentTime___jR8W">
                                  {new Date(child.created_at).toLocaleDateString("vi-VN")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </div>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {open && productId && (
        <SendModalComponent
          productId={productId}
          onClose={(refresh?: boolean) => {
            setOpen(false);
            if (refresh) loadComments();
          }}
        />
      )}
    </div>
  );
}